import { useState } from "react";
import { api, ago } from "../../lib/api.ts";
import { useApi } from "../../lib/useApi.ts";
import { Loading, ScreenHead, Table, Tile, useStaff } from "./ConsoleLayout.tsx";

// ---------------------------------------------------------------- devices

interface Device { id: string; model: string | null; arch: string | null; release: string | null; channel: string; last_seen: string | null; install_source: string | null; account: string | null }
interface DeviceDetail { device: Device; events: { received_at: string; type: string; payload: string }[] }

export function Devices() {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const list = useApi<{ devices: Device[] }>(`/api/console/devices?q=${encodeURIComponent(q)}`);
  const detail = useApi<DeviceDetail>(sel ? `/api/console/devices/${sel}` : null);
  return (
    <div className="flex flex-col gap-4">
      <ScreenHead label="Devices" />
      <input className="input max-w-md" type="search" placeholder="Search ID, model, version, account…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search devices" />
      {!list.data ? <Loading error={list.error} /> : (
        <Table>
          <thead><tr><th>Device</th><th>Model</th><th>Version</th><th>Channel</th><th>Last seen</th><th>Source</th><th>Account</th></tr></thead>
          <tbody>
            {list.data.devices.map((d) => (
              <tr key={d.id} onClick={() => setSel(d.id)} className={`cursor-pointer hover:bg-glass ${sel === d.id ? "bg-glass" : ""}`}>
                <td className="font-mono text-[13px]">{d.id.slice(0, 8)}…</td><td>{d.model ?? "–"} <span className="text-[13px] text-ink-3">{d.arch}</span></td>
                <td className="font-mono">{d.release}</td><td>{d.channel}</td><td>{ago(d.last_seen)}</td><td>{d.install_source ?? "–"}</td><td>{d.account ?? "–"}</td>
              </tr>
            ))}
            {!list.data.devices.length && <tr><td colSpan={7} className="text-ink-3">No devices match.</td></tr>}
          </tbody>
        </Table>
      )}
      {detail.data && (
        <section className="card p-5">
          <h2 className="mb-2 text-[17px]">Timeline for <span className="font-mono">{detail.data.device.id}</span></h2>
          <ol className="flex flex-col divide-y divide-dashed divide-line text-[15px]">
            {detail.data.events.map((e, i) => (
              <li key={i} className="grid gap-2 py-1.5 sm:grid-cols-[10rem_12rem_1fr]">
                <span className="font-mono text-[13px] text-ink-3">{e.received_at.replace("T", " ").slice(0, 16)}</span>
                <span className="font-mono text-[13px]">{e.type}</span>
                <span className="truncate font-mono text-[13px] text-ink-2">{e.payload}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- crashes

interface Crash { signature: string; package: string; device_count: number; first_release: string; fixed_in: string | null; owner: string | null; status: string }
const OWNERS = ["Unassigned", "Shell", "App", "Model", "Distro"];

export function Crashes() {
  const { canOperate } = useStaff();
  const { data, error, reload } = useApi<{ crashes: Crash[] }>("/api/console/crashes");
  const patch = async (sig: string, body: object) => { await api(`/api/console/crashes/${sig}`, { method: "PATCH", json: body }); await reload(); };
  if (!data) return <><ScreenHead label="Crashes" /><Loading error={error} /></>;
  return (
    <div className="flex flex-col gap-4">
      <ScreenHead label="Crashes" />
      <Table>
        <thead><tr><th>Signature</th><th>Package</th><th>Devices</th><th>First seen in</th><th>Fixed in</th><th>Owner</th><th>Status</th></tr></thead>
        <tbody>
          {data.crashes.map((c) => (
            <tr key={c.signature}>
              <td className="font-mono text-[13px]">{c.signature}</td><td className="font-mono text-[13px]">{c.package}</td><td className="font-bold text-ink">{c.device_count}</td>
              <td className="font-mono">{c.first_release}</td><td className="font-mono">{c.fixed_in ?? "–"}</td>
              <td><select disabled={!canOperate} className="input min-h-9 py-1 text-[15px]" value={c.owner ?? "Unassigned"} onChange={(e) => void patch(c.signature, { owner: e.target.value })}>{OWNERS.map((o) => <option key={o}>{o}</option>)}</select></td>
              <td><select disabled={!canOperate} className="input min-h-9 py-1 text-[15px]" value={c.status} onChange={(e) => void patch(c.signature, { status: e.target.value })}>{["new", "open", "fixed", "wontfix"].map((o) => <option key={o}>{o}</option>)}</select></td>
            </tr>
          ))}
          {!data.crashes.length && <tr><td colSpan={7} className="text-ink-3">No crash group has reached 3 devices.</td></tr>}
        </tbody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------- feedback

interface Fb { id: number; rating: number; category: string; message: string; device_id: string | null; release: string | null; owner: string | null; status: string; created_at: string }

export function Feedback() {
  const { canOperate } = useStaff();
  const { data, error, reload } = useApi<{ feedback: Fb[] }>("/api/console/feedback");
  const patch = async (id: number, body: object) => { await api(`/api/console/feedback/${id}`, { method: "PATCH", json: body }); await reload(); };
  if (!data) return <><ScreenHead label="Feedback" /><Loading error={error} /></>;
  return (
    <div className="flex flex-col gap-4">
      <ScreenHead label="Feedback" />
      <Table>
        <thead><tr><th>#</th><th>Rating</th><th>Category</th><th>Message</th><th>Version</th><th>Owner</th><th>Status</th></tr></thead>
        <tbody>
          {data.feedback.map((f) => (
            <tr key={f.id}>
              <td className="font-mono">{f.id}</td><td className="text-warn" aria-label={`${f.rating} of 5`}>{"★".repeat(f.rating)}</td><td>{f.category}</td>
              <td className="min-w-72">{f.message}<div className="text-[13px] text-ink-3">{ago(f.created_at)}</div></td><td className="font-mono">{f.release ?? "–"}</td>
              <td><select disabled={!canOperate} className="input min-h-9 py-1 text-[15px]" value={f.owner ?? "Unassigned"} onChange={(e) => void patch(f.id, { owner: e.target.value })}>{["Unassigned", "Product", "Ops", "Shell", "Model"].map((o) => <option key={o}>{o}</option>)}</select></td>
              <td><select disabled={!canOperate} className="input min-h-9 py-1 text-[15px]" value={f.status} onChange={(e) => void patch(f.id, { status: e.target.value })}>{["new", "triaged", "answered", "shipped"].map((o) => <option key={o}>{o}</option>)}</select></td>
            </tr>
          ))}
          {!data.feedback.length && <tr><td colSpan={7} className="text-ink-3">No feedback yet.</td></tr>}
        </tbody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------- funnel

interface FunnelData { from: string; to: string; signups: number; verified: number; downloaders: number; first_boots: number }

export function Funnel() {
  const { data, error } = useApi<FunnelData>("/api/console/funnel");
  if (!data) return <><ScreenHead label="Funnel" /><Loading error={error} /></>;
  const rows = [["Sign-ups", data.signups], ["Verified emails", data.verified], ["Downloaded ISO", data.downloaders], ["First boots", data.first_boots]] as const;
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <div className="flex flex-col gap-4">
      <ScreenHead label="Funnel" />
      <section className="card p-5">
        <h2 className="mb-3 text-[17px]">{data.from} to {data.to}</h2>
        <div className="flex flex-col gap-2">
          {rows.map(([label, n], i) => (
            <div key={label} className="grid grid-cols-[8rem_1fr_6rem] items-center gap-3 text-[15px]">
              <span className="text-ink-2">{label}</span>
              <div><div className="h-6 rounded-r bg-planet" style={{ width: `${(n / max) * 100}%` }} /></div>
              <span className="text-right tabular-nums">{n.toLocaleString()}{i > 0 && rows[i - 1]![1] ? <small className="block text-[13px] text-ink-3">{Math.round((n / rows[i - 1]![1]) * 100)}% of previous</small> : null}</span>
            </div>
          ))}
        </div>
      </section>
      <p className="text-[13px] text-ink-3">Internal accounts are excluded. Shared copies of the ISO also produce first boots, so first boots can exceed downloads.</p>
    </div>
  );
}

// ---------------------------------------------------------------- approvals

interface Approvals {
  applications: { id: string; kind: string; org_name: string | null; email: string; country: string | null; seats: number | null; note: string | null; status: string; created_at: string }[];
  refusals: { masked: string; domain: string; reason: string; at: string; blocked: number }[];
  signups_7d: { verified: number; waiting: number; refused: number; internal: number };
}

export function Approvals() {
  const { canOperate } = useStaff();
  const { data, error, reload } = useApi<Approvals>("/api/console/approvals");
  const [msg, setMsg] = useState<string | null>(null);
  const decide = async (id: string, d: "approve" | "decline") => {
    const r = await api<{ status: string; codes_issued?: number }>(`/api/console/applications/${id}/${d}`, { json: {} });
    setMsg(r.codes_issued ? `Approved. ${r.codes_issued} activation codes issued.` : `Application ${r.status}.`);
    await reload();
  };
  const block = async (domain: string) => {
    const r = await api<{ links_withdrawn: number }>("/api/console/blocked-domains", { json: { domain } });
    setMsg(`${domain} blocked. ${r.links_withdrawn} download links withdrawn.`);
    await reload();
  };
  if (!data) return <><ScreenHead label="Approvals" /><Loading error={error} /></>;
  return (
    <div className="flex flex-col gap-6">
      <ScreenHead label="Approvals" />
      {msg && <p role="status" className="rounded-xl bg-glass px-4 py-2.5 text-[15px] text-ink">{msg}</p>}
      <Table>
        <thead><tr><th>Kind</th><th>Name</th><th>Applicant</th><th>Country</th><th>Devices</th><th>Note</th><th>Status</th><th /></tr></thead>
        <tbody>
          {data.applications.map((a) => (
            <tr key={a.id}>
              <td className="capitalize">{a.kind}</td><td>{a.org_name ?? "–"}</td><td>{a.email}</td><td>{a.country ?? "–"}</td><td>{a.seats ?? "–"}</td>
              <td className="min-w-48">{a.note}<div className="text-[13px] text-ink-3">{ago(a.created_at)}</div></td><td>{a.status}</td>
              <td className="whitespace-nowrap text-right">{canOperate && a.status === "pending" && (<>
                <button className="btn btn-primary" onClick={() => void decide(a.id, "approve")}>{a.kind === "tester" ? "Add to testers" : `Approve, issue ${a.seats ?? 1} codes`}</button>
                <button className="btn ml-2" onClick={() => void decide(a.id, "decline")}>Decline</button>
              </>)}</td>
            </tr>
          ))}
          {!data.applications.length && <tr><td colSpan={8} className="text-ink-3">No applications yet.</td></tr>}
        </tbody>
      </Table>

      <section className="flex flex-col gap-3">
        <h2 className="text-[17px]">Download sign-ups, last 7 days</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Tile label="Verified" value={data.signups_7d.verified} /><Tile label="Waiting for code" value={data.signups_7d.waiting} />
          <Tile label="Refused at screening" value={data.signups_7d.refused} /><Tile label="Internal (staff)" value={data.signups_7d.internal} note="left out of metrics" />
        </div>
        <Table>
          <thead><tr><th>Address</th><th>Reason</th><th>When</th><th /></tr></thead>
          <tbody>
            {data.refusals.map((r, i) => (
              <tr key={i}>
                <td className="font-mono">{r.masked}</td><td>{r.reason.replaceAll("_", " ")}</td><td>{ago(r.at)}</td>
                <td className="text-right">{r.blocked ? <span className="pill bg-glass text-ink-3">blocked</span> : canOperate && r.reason !== "invalid" && <button className="btn btn-danger" onClick={() => void block(r.domain)}>Block {r.domain}</button>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </section>
    </div>
  );
}
